import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Award, Zap, TrendingUp, Clock, CheckCircle2, RefreshCw, ShoppingCart, Info } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DEFAULT_BOOST_PACKAGES = [
    { credits: 5, price: 99, label: 'Starter', desc: '5 créditos de boost' },
    { credits: 15, price: 249, label: 'Pro', desc: '15 créditos de boost', popular: true },
    { credits: 40, price: 549, label: 'Max', desc: '40 créditos de boost' },
];

const DEFAULT_BOOST_DURATIONS = [
    { days: 3, credits: 1, label: '3 días' },
    { days: 7, credits: 2, label: '7 días', popular: true },
    { days: 15, credits: 4, label: '15 días' },
    { days: 30, credits: 7, label: '30 días' },
];

function normalizeDurations(raw) {
    if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_BOOST_DURATIONS;
    return raw.map((d, i) => ({
        days: d.days,
        credits: d.credits,
        label: d.label || `${d.days} días`,
        popular: d.popular ?? (i === 1),
    }));
}

function normalizePackages(raw) {
    if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_BOOST_PACKAGES;
    return raw.map((p, i) => ({
        credits: p.credits,
        price: p.price_mxn ?? p.price ?? 0,
        label: p.label || `${p.credits} créditos`,
        desc: p.desc || `${p.credits} créditos de boost`,
        popular: p.popular ?? (i === 1),
    }));
}

export default function DashboardOkPro() {
    const [status, setStatus] = useState(null);
    const [myAds, setMyAds] = useState([]);
    const [boosts, setBoosts] = useState([]);
    const [boostPackages, setBoostPackages] = useState(DEFAULT_BOOST_PACKAGES);
    const [boostDurations, setBoostDurations] = useState(DEFAULT_BOOST_DURATIONS);
    const [loading, setLoading] = useState(true);
    const [boostOpen, setBoostOpen] = useState(false);
    const [buyOpen, setBuyOpen] = useState(false);
    const [boostForm, setBoostForm] = useState({ ad_id: '__none__', duration_days: 7 });
    const [buyPkg, setBuyPkg] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: st }, { data: ads }, { data: bts }, { data: pricing }] = await Promise.all([
                api.get('/me/okpro/status'),
                api.get('/me/ads', { params: { limit: 50, status: 'published' } }),
                api.get('/me/okpro/boosts'),
                api.get('/me/okpro/pricing'),
            ]);
            setStatus(st);
            setMyAds(Array.isArray(ads?.ads || ads) ? (ads?.ads || ads) : []);
            setBoosts(Array.isArray(bts) ? bts : []);
            if (pricing) {
                setBoostPackages(normalizePackages(pricing.packages));
                setBoostDurations(normalizeDurations(pricing.durations));
            }
        } catch (e) {
            toast.error('Error al cargar OkPro');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doBoost = async () => {
        if (boostForm.ad_id === '__none__') { toast.error('Selecciona un anuncio'); return; }
        setBusy(true);
        try {
            await api.post('/me/okpro/boost', { ad_id: boostForm.ad_id, duration_days: Number(boostForm.duration_days) });
            toast.success('¡Anuncio boosteado! Aparecerá destacado en búsquedas.');
            setBoostOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    const doBuy = async () => {
        if (!buyPkg) return;
        setBusy(true);
        try {
            await api.post('/me/okpro/buy-credits', { credits: buyPkg.credits, price_mxn: buyPkg.price });
            toast.success(`¡Compraste ${buyPkg.credits} créditos OkPro!`);
            setBuyOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    const selectedDuration = boostDurations.find(d => d.days === Number(boostForm.duration_days)) || boostDurations[1] || boostDurations[0];

    if (loading) return <div className="p-6 text-sm text-muted-foreground">Cargando OkPro…</div>;

    const credits = status?.credits_remaining ?? 0;
    const planEnabled = status?.plan_enabled ?? false;

    return (
        <div className="space-y-6" data-testid="dashboard-okpro">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Award className="h-6 w-6 text-amber-500" />
                    <h1 className="text-2xl font-semibold">OkPro · Super Boost</h1>
                </div>
                <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Actualizar</Button>
            </div>

            {!planEnabled && (
                <Alert className="border-amber-200 bg-amber-50">
                    <Info className="h-4 w-4" />
                    <AlertDescription>OkPro (boost) está disponible en planes de pago. <a href="/planes" className="underline font-medium">Actualiza tu plan</a> o compra créditos adicionales.</AlertDescription>
                </Alert>
            )}

            {/* Credits + Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-amber-400 to-amber-600" />
                    <CardContent className="p-5">
                        <div className="text-xs uppercase text-muted-foreground tracking-wider">Créditos disponibles</div>
                        <div className="text-4xl font-bold text-amber-600 mt-1">{credits}</div>
                        <div className="text-xs text-muted-foreground mt-1">Se renuevan cada mes con tu plan</div>
                    </CardContent>
                </Card>
                <Card className="overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-rose-400 to-rose-600" />
                    <CardContent className="p-5">
                        <div className="text-xs uppercase text-muted-foreground tracking-wider">Boosts activos</div>
                        <div className="text-4xl font-bold text-rose-600 mt-1">{boosts.filter(b => b.status === 'active').length}</div>
                        <div className="text-xs text-muted-foreground mt-1">Anuncios destacados ahora</div>
                    </CardContent>
                </Card>
                <Card className="overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-emerald-400 to-emerald-600" />
                    <CardContent className="p-5">
                        <div className="text-xs uppercase text-muted-foreground tracking-wider">Total boosteado</div>
                        <div className="text-4xl font-bold text-emerald-600 mt-1">{boosts.length}</div>
                        <div className="text-xs text-muted-foreground mt-1">Histórico de boosts</div>
                    </CardContent>
                </Card>
            </div>

            {/* How it works */}
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="p-5">
                    <div className="font-semibold mb-2 flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" />¿Cómo funciona OkPro Boost?</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                        <div className="flex items-start gap-2"><div className="h-6 w-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold shrink-0">1</div><div><div className="font-medium">Selecciona un anuncio</div><div className="text-muted-foreground text-xs">Elige cuál de tus anuncios publicados quieres destacar</div></div></div>
                        <div className="flex items-start gap-2"><div className="h-6 w-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold shrink-0">2</div><div><div className="font-medium">Elige la duración</div><div className="text-muted-foreground text-xs">De 3 a 30 días. Cada boost consume créditos de tu saldo</div></div></div>
                        <div className="flex items-start gap-2"><div className="h-6 w-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold shrink-0">3</div><div><div className="font-medium">Mayor visibilidad</div><div className="text-muted-foreground text-xs">Tu anuncio aparece primero en búsquedas y categorías</div></div></div>
                    </div>
                </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-3 flex-wrap">
                <Button onClick={() => setBoostOpen(true)} disabled={credits <= 0} className="bg-amber-500 hover:bg-amber-600" data-testid="boost-ad-btn">
                    <Zap className="h-4 w-4 mr-2" />Boost un anuncio ({credits} créd. disponibles)
                </Button>
                <Button variant="outline" onClick={() => setBuyOpen(true)} data-testid="buy-credits-btn">
                    <ShoppingCart className="h-4 w-4 mr-2" />Comprar créditos extra
                </Button>
            </div>

            {/* Active boosts list */}
            <Tabs defaultValue="active">
                <TabsList>
                    <TabsTrigger value="active">Activos ({boosts.filter(b => b.status === 'active').length})</TabsTrigger>
                    <TabsTrigger value="all">Historial ({boosts.length})</TabsTrigger>
                </TabsList>
                {[['active', boosts.filter(b => b.status === 'active')], ['all', boosts]].map(([key, items]) => (
                    <TabsContent key={key} value={key} className="pt-4 space-y-3">
                        {items.length === 0 ? (
                            <div className="rounded-2xl border border-dashed py-12 text-center text-sm text-muted-foreground">
                                {key === 'active' ? 'No tienes boosts activos en este momento.' : 'No has creado ningún boost aún.'}
                            </div>
                        ) : items.map(b => (
                            <div key={b.id} className="rounded-xl border bg-card p-4 flex items-center gap-4" data-testid={`boost-${b.id}`}>
                                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                                    <Zap className="h-5 w-5 text-amber-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">{b.ad_title || b.ad_id}</div>
                                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{b.duration_days} días</span>
                                        {b.ends_at && <span>Vence: {new Date(b.ends_at).toLocaleDateString('es-MX')}</span>}
                                        <span>{b.credits_used} crédito(s) usados</span>
                                    </div>
                                </div>
                                {b.status === 'active'
                                    ? <Badge className="bg-amber-100 text-amber-800 border-amber-300"><CheckCircle2 className="h-3 w-3 mr-1" />Activo</Badge>
                                    : <Badge variant="outline" className="text-muted-foreground">{b.status}</Badge>}
                            </div>
                        ))}
                    </TabsContent>
                ))}
            </Tabs>

            {/* Boost dialog */}
            <Dialog open={boostOpen} onOpenChange={setBoostOpen}>
                <DialogContent data-testid="boost-dialog">
                    <DialogHeader><DialogTitle><Zap className="h-5 w-5 inline mr-2 text-amber-500" />Boost a anuncio</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Anuncio a destacar</Label>
                            <Select value={boostForm.ad_id} onValueChange={(v) => setBoostForm(f => ({ ...f, ad_id: v }))}>
                                <SelectTrigger data-testid="boost-ad-select"><SelectValue placeholder="Selecciona un anuncio…" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">Selecciona un anuncio…</SelectItem>
                                    {myAds.map(a => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Duración del boost</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                {boostDurations.map(d => (
                                    <button key={d.days} type="button"
                                        onClick={() => setBoostForm(f => ({ ...f, duration_days: d.days }))}
                                        className={`rounded-xl border-2 p-3 text-left transition-all ${Number(boostForm.duration_days) === d.days ? 'border-amber-500 bg-amber-50' : 'border-border hover:border-amber-300'}`}>
                                        <div className="font-semibold text-sm">{d.label}</div>
                                        <div className="text-xs text-muted-foreground">{d.credits} crédito(s)</div>
                                        {d.popular && <Badge className="mt-1 bg-amber-100 text-amber-800 text-[9px]">Popular</Badge>}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="rounded-lg bg-secondary/50 p-3 text-sm flex items-center justify-between">
                            <span>Créditos necesarios</span>
                            <span className="font-bold">{selectedDuration.credits} de {credits} disponibles</span>
                        </div>
                        {selectedDuration.credits > credits && (
                            <Alert className="border-red-200 bg-red-50"><AlertDescription className="text-red-700 text-xs">Créditos insuficientes. Compra más créditos para este boost.</AlertDescription></Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setBoostOpen(false)}>Cancelar</Button>
                        <Button onClick={doBoost} disabled={busy || boostForm.ad_id === '__none__' || selectedDuration.credits > credits} className="bg-amber-500 hover:bg-amber-600">
                            {busy ? 'Activando…' : `Activar boost (${selectedDuration.credits} créd.)`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Buy credits dialog */}
            <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
                <DialogContent data-testid="buy-credits-dialog">
                    <DialogHeader><DialogTitle><ShoppingCart className="h-5 w-5 inline mr-2" />Comprar créditos OkPro</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">Elige un paquete. El pago se descuenta de tu OkWallet.</div>
                        <div className="grid gap-3">
                            {boostPackages.map(pkg => (
                                <button key={pkg.credits} type="button"
                                    onClick={() => setBuyPkg(pkg)}
                                    className={`rounded-xl border-2 p-4 text-left transition-all ${buyPkg?.credits === pkg.credits ? 'border-amber-500 bg-amber-50' : 'border-border hover:border-amber-300'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-bold">{pkg.label} — {pkg.credits} créditos</div>
                                            <div className="text-xs text-muted-foreground">{pkg.desc}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-lg">${fmt(pkg.price)}</div>
                                            <div className="text-[10px] text-muted-foreground">MXN</div>
                                        </div>
                                    </div>
                                    {pkg.popular && <Badge className="mt-2 bg-amber-100 text-amber-800 text-[9px]">Más popular</Badge>}
                                </button>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setBuyOpen(false)}>Cancelar</Button>
                        <Button onClick={doBuy} disabled={busy || !buyPkg} className="bg-amber-500 hover:bg-amber-600">
                            {busy ? 'Procesando…' : `Comprar $${fmt(buyPkg?.price || 0)}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
